import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api, ApiError } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { ExportButton } from '../../components/ExportButton';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { Alert, Card, EmptyState, PageHeader, Spinner } from '../../components/ui/display';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useAccountDisplayMode } from '../../hooks/useAccountDisplayMode';
import type { MarketingReport } from '../../types/models';
import { accountLabel } from '../../utils/accounts';
import { formatDate, formatMoney } from '../../utils/format';
import { Stat } from '../marketing/MarketingPage';
import { sources } from '../masters/sources';
import { compactInr, roasText } from './pnlParts';

/** Marketing spend by app × platform, by account and by day, with ROAS. */
export function MarketingReportPage() {
  const [range, setRange] = useDateFilter('thisMonth');
  const [appId, setAppId] = useState('');
  const mode = useAccountDisplayMode();
  const query = useQuery({
    queryKey: ['reports', 'marketing', range.startDate, range.endDate, appId],
    queryFn: () =>
      api.get<MarketingReport>('/reports/marketing', {
        startDate: range.startDate,
        endDate: range.endDate,
        appId: appId || undefined,
      }),
    placeholderData: keepPreviousData,
  });
  const r = query.data;

  return (
    <>
      <PageHeader
        title="Marketing report"
        description={`${formatDate(range.startDate)} – ${formatDate(range.endDate)} · spend before GST in INR; ROAS = revenue ÷ spend`}
        actions={
          <>
            <SearchableSelect
              ariaLabel="App"
              placeholder="All apps"
              clearable
              className="w-60"
              value={appId}
              remote={sources.app({})}
              onChange={setAppId}
            />
            <ExportButton
              report="marketing-report"
              params={{ startDate: range.startDate, endDate: range.endDate, appId }}
            />
            <DateRangeFilter value={range} onChange={setRange} />
          </>
        }
      />
      {query.isError && (
        <Alert tone="error">
          {query.error instanceof ApiError ? query.error.message : 'Failed to load'}
        </Alert>
      )}
      {query.isLoading && <Spinner />}
      {r && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Spend (before GST)" value={formatMoney(r.totals.spend)} />
            <Stat label="GST" value={formatMoney(r.totals.gst)} tone="gst" />
            <Stat label="Total incl. GST" value={formatMoney(r.totals.total)} />
            <Stat label="Revenue" value={formatMoney(r.totals.revenue)} tone="profit" />
            <Stat label="ROAS" value={roasText(r.totals.roas)} />
          </div>

          {r.apps.length === 0 ? (
            <Card>
              <EmptyState title="No marketing spend in this period" />
            </Card>
          ) : (
            <>
              <Card title="Daily spend" className="mb-6">
                <div style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={r.daily.map((d) => ({ date: d.date.slice(5), spend: Number(d.spend) }))}
                      margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={compactInr}
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        width={64}
                      />
                      <Tooltip formatter={(v) => [formatMoney(String(v)), 'Spend']} />
                      <Bar dataKey="spend" fill="#4f46e5" radius={[3, 3, 0, 0]} maxBarSize={32} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card title="By app and platform" bodyClassName="p-0" className="mb-6">
                <div className="relative overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                      <tr>
                        <th className="px-4 py-3 text-left">App</th>
                        {r.platforms.map((p) => (
                          <th key={p.id} className="px-4 py-3 text-right">
                            {p.name}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-right">Total spend</th>
                        <th className="px-4 py-3 text-right">Revenue</th>
                        <th className="px-4 py-3 text-right">ROAS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {r.apps.map((a) => (
                        <tr key={a.app.id}>
                          <td className="px-4 py-2">
                            <Link
                              to={`/apps/${a.app.id}`}
                              className="flex items-center gap-2 font-medium text-slate-900 hover:text-brand-700"
                            >
                              <AppIcon src={a.app.iconUrl} name={a.app.name} size={24} />
                              {a.app.name}
                            </Link>
                          </td>
                          {r.platforms.map((p) => (
                            <td key={p.id} className="num px-4 py-2 text-right">
                              {a.byPlatform[p.id] ? (
                                formatMoney(a.byPlatform[p.id])
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          ))}
                          <td className="num px-4 py-2 text-right font-medium">
                            {formatMoney(a.spend)}
                          </td>
                          <td className="num px-4 py-2 text-right text-emerald-700">
                            {formatMoney(a.revenue)}
                          </td>
                          <td className="num px-4 py-2 text-right">{roasText(a.roas)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-slate-200 bg-slate-50/60 font-semibold">
                      <tr>
                        <td className="px-4 py-2">Total</td>
                        {r.platforms.map((p) => (
                          <td key={p.id} className="num px-4 py-2 text-right">
                            {formatMoney(r.platformTotals[p.id] ?? '0')}
                          </td>
                        ))}
                        <td className="num px-4 py-2 text-right">{formatMoney(r.totals.spend)}</td>
                        <td className="num px-4 py-2 text-right">
                          {formatMoney(r.totals.revenue)}
                        </td>
                        <td className="num px-4 py-2 text-right">{roasText(r.totals.roas)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>

              <Card title="By account" bodyClassName="p-0">
                <div className="relative overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                      <tr>
                        <th className="px-4 py-3 text-left">Account</th>
                        <th className="px-4 py-3 text-left">App</th>
                        <th className="px-4 py-3 text-left">Billing</th>
                        <th className="px-4 py-3 text-right">Spend</th>
                        <th className="px-4 py-3 text-right text-violet-700">GST</th>
                        <th className="px-4 py-3 text-right">Total</th>
                        <th className="px-4 py-3 text-right">Days entered</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {r.accounts.map((a) => (
                        <tr key={`${a.account.id}|${a.app.id}`}>
                          <td className="px-4 py-2">
                            <Link
                              to={`/ads-accounts/${a.account.id}`}
                              className="font-medium text-slate-900 hover:text-brand-700"
                            >
                              {accountLabel(a.account, mode)}
                            </Link>
                            <p className="text-xs text-slate-400">{a.account.platform.name}</p>
                          </td>
                          <td className="px-4 py-2">
                            <span className="flex items-center gap-2">
                              <AppIcon src={a.app.iconUrl} name={a.app.name} size={22} />
                              {a.app.name}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-500">
                            {a.account.billingType === 'POSTPAID'
                              ? `Credit · ${a.account.managerAccount?.name ?? ''}`
                              : 'Prepaid'}
                          </td>
                          <td className="num px-4 py-2 text-right font-medium">
                            {formatMoney(a.spend)}
                          </td>
                          <td className="num px-4 py-2 text-right text-violet-700">
                            {formatMoney(a.gst)}
                          </td>
                          <td className="num px-4 py-2 text-right">{formatMoney(a.total)}</td>
                          <td className="num px-4 py-2 text-right">{a.daysEntered}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
}
